import {
  BaseFullSchema_OLD,
  BaseRefSchema,
  BaseCreateSchema,
  BaseUpdateSchema,
  PaginatedResponse,
  IdAsUUID,
} from "./common/commonTypes";
import {
  PrimaryOrderStatusType,
  ApprovalStatusType,
} from "./common/statusTypes";
import { ItemRef } from "./itemTypes";
import { UOMUnitRef } from "./uomTypes";
import { InvMovRef } from "./invMovementTypes";
import { LocationRef } from "./locationTypes";
import { BOMRevRef } from "./bomTypes";
import { POLineRef } from "./purchOrderTypes";

export const WORK_ORDER_BUILD_STATUSES = [
  "APPROVAL",
  "REJECTED",
  "READY",
  "IN_PROCESS",
  "COMPLETE",
  "VOID",
] as const;
export type WorkOrderBuildStatusType =
  (typeof WORK_ORDER_BUILD_STATUSES)[number];

export const WORK_ORD_PART_STATUSES = [
  "PENDING",
  "PARTIAL",
  "COMPLETE",
  "VOID",
] as const;
export type WorkOrderPartStatusType = (typeof WORK_ORD_PART_STATUSES)[number];

export interface WorkOrderFull extends BaseFullSchema_OLD {
  number: string;
  pallet_name: string | null;

  item: ItemRef;
  bom_revision: BOMRevRef | null;
  target_revision: string;
  location: LocationRef;
  uom_unit: UOMUnitRef;

  box_folder_id: string | null;

  pallet_work_order_id: string | null;

  is_rework: boolean;
  is_planned_deviation: boolean;
  is_eng_build: boolean;
  is_bioburden: boolean;

  quantity: string;
  order_status: PrimaryOrderStatusType;
  approval_status: ApprovalStatusType;
  build_status: WorkOrderBuildStatusType;
  part_status: WorkOrderPartStatusType;

  wo_date: string;
  planned_start_date: string;
  planned_completion_date: string;
  actual_start_date: string | null;
  actual_completion_date: string | null;

  notes: string | null;

  components: WOCompFull[];
  production_review: ProdRevRef | null;
  purchase_order_line: POLineRef | null;
}

export interface WorkOrderListView extends BaseFullSchema_OLD {
  number: string;
  pallet_name: string | null;
  is_rework: boolean;
  is_planned_deviation: boolean;
  is_eng_build: boolean;
  is_bioburden: boolean;

  item_name: string;
  target_revision: string;
  target_quantity: string;
  built_quantity: string;

  order_status: PrimaryOrderStatusType;
  approval_status: ApprovalStatusType;
  build_status: WorkOrderBuildStatusType;
  part_status: WorkOrderPartStatusType;

  wo_date: string;
  planned_start_date: string;
  planned_completion_date: string;
  actual_start_date?: string;
  actual_completion_date?: string;
}

export interface WorkOrderRef extends BaseRefSchema {
  number: string;
  is_rework: boolean;
  quantity: string;
  order_status: PrimaryOrderStatusType;
  approval_status: ApprovalStatusType;
  build_status: WorkOrderBuildStatusType;
}

export interface WorkOrderCreate extends BaseCreateSchema {
  number: string | null;
  pallet_name: string | null;

  item_id: IdAsUUID;
  bom_revision_id: IdAsUUID | null;
  target_revision: string;
  location_id: IdAsUUID;
  uom_unit_id: IdAsUUID;

  pallet_work_order_id: IdAsUUID | null;

  is_rework: boolean;
  is_planned_deviation: boolean;
  is_eng_build: boolean;
  is_bioburden: boolean;

  quantity: number;

  wo_date: string;
  planned_start_date: string;
  planned_completion_date: string;

  notes: string | null;

  component_data: WOCompCreate[];
}

export interface WorkOrderUpdate {
  number?: string | null;
  pallet_name?: string | null;

  item_id?: IdAsUUID;
  bom_revision_id?: IdAsUUID | null;
  target_revision?: string;
  location_id?: IdAsUUID;
  uom_unit_id?: IdAsUUID;

  pallet_work_order_id?: IdAsUUID | null;

  is_rework?: boolean;
  is_planned_deviation?: boolean;
  is_eng_build?: boolean;
  is_bioburden?: boolean;

  quantity?: number;

  planned_start_date?: string;
  planned_completion_date?: string;

  notes?: string | null;

  lines_to_update: WOCompUpdate[];
  lines_to_create: WOCompCreate[];
  lines_to_delete: IdAsUUID[];
}

export interface WOCompFull extends BaseFullSchema_OLD {
  line: number;
  quantity_per: string;
  quantity_per_base: string;
  quantity_required: string;
  quantity_required_base: string;
  quantity_allocated: string;
  quantity_allocated_base: string;
  quantity_offset: string;
  bom_rev_id?: string;
  notes?: string;

  component_item: ItemRef;
  uom_unit: UOMUnitRef;
  alloc_moves: InvMovRef[];
}

export interface WOCompRef extends BaseFullSchema_OLD {
  line: number;
  quantity_per: string;
  quantity_required: string;
  quantity_allocated: string;
  quantity_offset: string;
  bom_rev_id: string | null;
  notes: string | null;

  component_item: ItemRef;
  uom_unit: UOMUnitRef;
}

export interface WOCompCreate extends BaseCreateSchema {
  line: number | null;
  work_order_id: IdAsUUID | null;
  component_item_id: IdAsUUID;
  uom_unit_id: IdAsUUID;
  quantity_per: number;
  quantity_required: number;
  quantity_offset: number;
  bom_rev_id: string | null;
  notes: string | null;
}

export interface WOCompUpdate extends BaseUpdateSchema {
  line_id: IdAsUUID;
  component_item_id?: IdAsUUID;
  uom_unit_id?: IdAsUUID;
  quantity_per?: number;
  quantity_required?: number;
  quantity_allocated?: number;
  quantity_offset?: number;
  notes?: string | null;
}

export interface WOCompConsumption {
  wo_comp_id: IdAsUUID;
  inventory_position_id: IdAsUUID;
  uom_unit_id: IdAsUUID;
  quantity: number;
}

export interface ProdRevCreate extends BaseCreateSchema {
  work_order_id: IdAsUUID;
  quantity_produced: number;
  revision_built: string;
  right_first_time: boolean;
  planned_deviation: boolean;
  rework: boolean;
  actual_start_date: string;
  actual_completion_date: string;
  labor_cost: number | null;
  overhead_cost: number | null;
  expiration_date: string | null;
  notes: string | null;
  linked_tgcm: IdAsUUID[];
}

export interface ProdRevRef extends BaseRefSchema {
  quantity_produced: string;
  revision_built: string;
  right_first_time: boolean;
  planned_deviation: boolean;
  rework: boolean;
  actual_start_date: string;
  actual_completion_date: string;
  expiration_date: string | null;
  uom_unit: UOMUnitRef;
  notes: string | null;
}

export type PaginatedWOResponse = PaginatedResponse<WorkOrderListView>;
